import React, { useState } from 'react';
import { User, Bell, Shield, CreditCard, Trash2, ChevronRight, Check, Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
function Toggle({
  checked,
  onChange
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <button onClick={() => onChange(!checked)} className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${checked ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-648">
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-649" />
    </button>;
}
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden" data-id="element-650">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-border" data-id="element-651">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-652">
          {title}
        </h3>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-dark-border" data-id="element-653">
        {children}
      </div>
    </div>;
}
function SettingRow({
  label,
  description,
  children
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <div className="flex items-center justify-between px-6 py-4" data-id="element-654">
      <div data-id="element-655">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-656">
          {label}
        </p>
        {description && <p className="text-xs text-gray-400 mt-0.5" data-id="element-657">{description}</p>}
      </div>
      {children}
    </div>;
}
export function SettingsView() {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const [notifications, setNotifications] = useState({
    budgetAlerts: true,
    goalProgress: true,
    weeklyReport: false,
    unusualActivity: true,
    marketUpdates: false
  });
  const [profile, setProfile] = useState({
    name: 'Alex Johnson',
    email: 'alex@example.com',
    phone: '+1 (555) 123-4567'
  });
  const connectedBanks = [{
    name: 'Chase Bank',
    type: 'Checking & Savings',
    icon: '🏦',
    lastSync: '2 min ago'
  }, {
    name: 'American Express',
    type: 'Credit Card',
    icon: '💳',
    lastSync: '5 min ago'
  }, {
    name: 'Fidelity',
    type: 'Investment Account',
    icon: '📈',
    lastSync: '1 hour ago'
  }];
  return <div className="space-y-6 max-w-2xl" data-id="element-658">
      {/* Profile */}
      <Section title="Profile" data-id="element-659">
        <div className="px-6 py-5" data-id="element-660">
          <div className="flex items-center gap-4 mb-5" data-id="element-661">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-white text-2xl font-bold" data-id="element-662">
              A
            </div>
            <div data-id="element-663">
              <p className="text-base font-semibold text-gray-900 dark:text-white" data-id="element-664">
                {profile.name}
              </p>
              <p className="text-sm text-gray-500" data-id="element-665">{profile.email}</p>
              <button className="text-xs text-primary-500 hover:text-primary-600 mt-1 font-medium" data-id="element-666">
                Change photo
              </button>
            </div>
          </div>
          <div className="space-y-3" data-id="element-667">
            {[{
            label: 'Full Name',
            value: profile.name,
            key: 'name'
          }, {
            label: 'Email',
            value: profile.email,
            key: 'email'
          }, {
            label: 'Phone',
            value: profile.phone,
            key: 'phone'
          }].map(field => <div key={field.key} data-id="element-668">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide" data-id="element-669">
                  {field.label}
                </label>
                <input type="text" value={field.value} onChange={e => setProfile(p => ({
              ...p,
              [field.key]: e.target.value
            }))} className="mt-1 w-full bg-gray-50 dark:bg-dark-hover border border-gray-200 dark:border-dark-border rounded-xl px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:border-primary-400 dark:focus:border-primary-500 transition-colors" data-id="element-670" />
              </div>)}
            <button className="mt-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors" data-id="element-671">
              Save Changes
            </button>
          </div>
        </div>
      </Section>

      {/* Appearance */}
      <Section title="Appearance" data-id="element-672">
        <SettingRow label="Theme" description="Choose your preferred color scheme" data-id="element-673">
          <div className="flex gap-2" data-id="element-674">
            {[{
            value: 'light',
            icon: Sun,
            label: 'Light'
          }, {
            value: 'dark',
            icon: Moon,
            label: 'Dark'
          }].map(opt => {
            const Icon = opt.icon;
            const isActive = theme === opt.value;
            return <button key={opt.value} onClick={toggleTheme} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${isActive ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-hover text-gray-600 dark:text-gray-400'}`} data-id="element-675">
                  <Icon size={13} data-id="element-676" /> {opt.label}
                </button>;
          })}
          </div>
        </SettingRow>
      </Section>

      {/* Notifications */}
      <Section title="Notifications" data-id="element-677">
        {Object.entries(notifications).map(([key, value]) => {
        const labels: Record<string, {
          label: string;
          desc: string;
        }> = {
          budgetAlerts: {
            label: 'Budget Alerts',
            desc: "Get notified when you're close to or over budget"
          },
          goalProgress: {
            label: 'Goal Progress',
            desc: 'Weekly updates on your financial goals'
          },
          weeklyReport: {
            label: 'Weekly Report',
            desc: "Summary of your week's financial activity"
          },
          unusualActivity: {
            label: 'Unusual Activity',
            desc: 'Alerts for suspicious or large transactions'
          },
          marketUpdates: {
            label: 'Market Updates',
            desc: 'Daily portfolio performance updates'
          }
        };
        return <SettingRow key={key} label={labels[key].label} description={labels[key].desc} data-id="element-678">
              <Toggle checked={value} onChange={v => setNotifications(n => ({
            ...n,
            [key]: v
          }))} data-id="element-679" />
            </SettingRow>;
      })}
      </Section>

      {/* Connected Accounts */}
      <Section title="Connected Accounts" data-id="element-680">
        {connectedBanks.map((bank, i) => <div key={i} className="flex items-center justify-between px-6 py-4" data-id="element-681">
            <div className="flex items-center gap-3" data-id="element-682">
              <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-dark-hover flex items-center justify-center text-lg" data-id="element-683">
                {bank.icon}
              </div>
              <div data-id="element-684">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-685">
                  {bank.name}
                </p>
                <p className="text-xs text-gray-400" data-id="element-686">
                  {bank.type} · Synced {bank.lastSync}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-687">
              <span className="flex items-center gap-1 text-xs text-accent-600 dark:text-accent-400 font-medium" data-id="element-688">
                <Check size={11} data-id="element-689" /> Connected
              </span>
              <button className="text-xs text-gray-400 hover:text-red-500 transition-colors" data-id="element-690">
                Remove
              </button>
            </div>
          </div>)}
        <div className="px-6 py-4" data-id="element-691">
          <button className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium" data-id="element-692">
            + Connect another account
          </button>
        </div>
      </Section>

      {/* Security */}
      <Section title="Security" data-id="element-693">
        <SettingRow label="Change Password" description="Last changed 3 months ago" data-id="element-694">
          <button className="text-xs text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1" data-id="element-695">
            Update <ChevronRight size={12} data-id="element-696" />
          </button>
        </SettingRow>
        <SettingRow label="Two-Factor Authentication" description="Add an extra layer of security" data-id="element-697">
          <Toggle checked={true} onChange={() => {}} data-id="element-698" />
        </SettingRow>
        <SettingRow label="Active Sessions" description="2 devices logged in" data-id="element-699">
          <button className="text-xs text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1" data-id="element-700">
            Manage <ChevronRight size={12} data-id="element-701" />
          </button>
        </SettingRow>
      </Section>

      {/* Danger zone */}
      <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 p-6" data-id="element-702">
        <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2" data-id="element-703">
          <Trash2 size={15} data-id="element-704" /> Danger Zone
        </h3>
        <p className="text-xs text-red-600 dark:text-red-400 mb-3" data-id="element-705">
          These actions are irreversible. Please proceed with caution.
        </p>
        <div className="flex gap-3" data-id="element-706">
          <button className="text-xs px-4 py-2 rounded-xl border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium" data-id="element-707">
            Export Data
          </button>
          <button className="text-xs px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-medium" data-id="element-708">
            Delete Account
          </button>
        </div>
      </div>
    </div>;
}